import { offset } from '@floating-ui/dom';
import { i18n } from 'i18n';
import Shepherd from 'shepherd.js';
import { StepOptions } from 'shepherd.js';
import state from 'store';
import termsAndConditions from 'store/termsAndConditions';
import { triggerTrackEvent } from 'utils/events';
import { getPreviousChat } from './utils';
import { providerConfState } from '../store/providerConf';
import initParams from 'store/initParams';

type ProductTourType = 'INITIAL' | 'FLIGHT_CARDS' | 'COPY_MESSAGE' | 'FAV_PRODUCT' | 'NEW_CHAT';

interface ActionButtons {
  text: string;
  action: () => void;
}

interface ParamsByType {
  storageItem: string;
  clickNext: string;
  clickBack: string;
  finishTour: string;
  closedTour: string;
  startTour: string;
}

const paramsByType: { [key in ProductTourType]: ParamsByType } = {
  INITIAL: {
    storageItem: 'finishTour',
    clickNext: 'product_tour_click_next',
    clickBack: 'product_tour_click_back',
    finishTour: 'product_tour_finish_completed',
    closedTour: 'product_tour_finish_closed',
    startTour: 'product_tour_start',
  },
  FLIGHT_CARDS: {
    storageItem: 'finishTourFlightCards',
    clickNext: 'flight_cards_click_next',
    clickBack: 'flight_cards_click_back',
    finishTour: 'flight_cards_finish_completed',
    closedTour: 'flight_cards_finish_closed',
    startTour: 'flight_cards_start',
  },
  COPY_MESSAGE: {
    storageItem: 'copyMessageTour',
    clickNext: 'copy_message_tour_click_next',
    clickBack: 'copy_message_tour_click_back',
    finishTour: 'copy_message_tour_finish_completed',
    closedTour: 'copy_message_tour_finish_closed',
    startTour: 'copy_message_tour_start',
  },
  FAV_PRODUCT: {
    storageItem: 'favProductTour',
    clickNext: 'fav_product_tour_click_next',
    clickBack: 'fav_product_tour_click_back',
    finishTour: 'fav_product_tour_finish_completed',
    closedTour: 'fav_product_tour_finish_closed',
    startTour: 'fav_product_tour_start',
  },
  NEW_CHAT: {
    storageItem: 'newChatTour',
    clickNext: 'new_chat_tour_click_next',
    clickBack: 'new_chat_tour_click_back',
    finishTour: 'new_chat_tour_finish_completed',
    closedTour: 'new_chat_tour_finish_closed',
    startTour: 'new_chat_tour_start',
  },
};

/**
 * Check if the product tour feature is enabled based on the current conditions.
 */
export const isEnabledProductTourFeature = (type: ProductTourType) => {
  const params = paramsByType[type],
    hasAcceptedTerms = termsAndConditions.accepted,
    isRecoverChat = state.previousChat !== null,
    isWidget = initParams.mode === 'BUBBLE',
    hasEnabledConfig = providerConfState.conf.features.includes('productTour'),
    alreadyFinishedTour = typeof localStorage !== 'undefined' && localStorage.getItem(params.storageItem) === 'true',
    isDisableTour = !hasAcceptedTerms || isWidget || !hasEnabledConfig || alreadyFinishedTour || isRecoverChat;

  return !isDisableTour;
};

/**
 * Initialize a product tour based on the specified type.
 * @param type
 * @param cbBeforeOpen
 * @param cbAfterClose
 */
const initTour = (type: ProductTourType, cbBeforeOpen: () => void = () => {}, cbAfterClose: () => void = () => {}) => {
  const params = paramsByType[type];

  if (!isEnabledProductTourFeature(type)) {
    console.debug(`Product tour ${type} is not starting, because not valid conditions`);
    return;
  }

  console.debug(`Product tour ${type} is starting`);

  const buildButtons = (tour: Shepherd.Tour, hasNext: boolean, hasPrev: boolean) => {
    const buttons: ActionButtons[] = [];
    if (hasPrev) {
      buttons.push({
        text: `<p class='eva-3-link'>${i18n.t.productTour.prev}</p>`,
        action: () => {
          triggerTrackEvent({
            event_type: 'user',
            event_name: params.clickBack,
          });
          tour.back();
        },
      });
    }

    if (hasNext) {
      buttons.push({
        text: `<p class='eva-3-link'>${i18n.t.productTour.next}</p>`,
        action: () => {
          triggerTrackEvent({
            event_type: 'user',
            event_name: params.clickNext,
          });
          tour.next();
        },
      });
    } else if ((!hasNext && hasPrev) || (!hasNext && !hasPrev)) {
      buttons.push({
        text: `<p class='eva-3-link'>${i18n.t.productTour.close}</p>`,
        action: () => {
          triggerTrackEvent({
            event_type: 'user',
            event_name: params.clickNext,
          });
          tour.next();
        },
      });
    }

    return buttons;
  };

  const saveFinishTour = (isCompleted: boolean) => {
    triggerTrackEvent({
      event_type: 'user',
      event_name: isCompleted ? params.finishTour : params.closedTour,
    });

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(params.storageItem, 'true');
    }
  };

  const getStepsByType = (type: ProductTourType, tour: Shepherd.Tour): StepOptions[] => {
    const isMobile = initParams.isMobile;

    switch (type) {
      case 'INITIAL':
        return [
          {
            title: i18n.t.productTour.initTour.step1.title,
            text: isMobile ? i18n.t.productTour.initTour.step1.text_mobile : i18n.t.productTour.initTour.step1.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.textarea-container .send-message-container',
              on: 'top',
            },
            scrollTo: false, // This is necessary to prevent scroll down
            buttons: buildButtons(tour, true, false),
          },
          {
            title: i18n.t.productTour.initTour.step2.title,
            text: i18n.t.productTour.initTour.step2.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.conversation-container .context .grid-template',
              on: 'top',
            },
            buttons: buildButtons(tour, true, true),
          },
          {
            title: i18n.t.productTour.initTour.step3.title,
            text: isMobile ? i18n.t.productTour.initTour.step3.text_mobile : i18n.t.productTour.initTour.step3.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.chat-cards-container .chat-card-element:first-child',
              on: isMobile ? 'top' : 'right',
            },
            buttons: buildButtons(tour, false, true),
          },
        ];
      case 'FLIGHT_CARDS':
        return [
          {
            title: i18n.t.productTour.flightOffers.step1.title,
            text: i18n.t.productTour.flightOffers.step1.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.offer-group-cards-container .offer-group-card.flight',
              on: isMobile ? 'bottom' : 'right',
            },
            scrollTo: false,
            buttons: buildButtons(tour, false, false),
          },
        ];
      case 'COPY_MESSAGE':
        return [
          {
            title: i18n.t.productTour.copyMessage.step1.title,
            text: i18n.t.productTour.copyMessage.step1.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.textarea-container .send-message-container',
              on: 'top',
            },
            buttons: buildButtons(tour, false, false),
            scrollTo: false,
          },
        ];
      case 'FAV_PRODUCT':
        return [
          {
            title: i18n.t.productTour.favProduct.step1.title,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.favorite-button',
              on: isMobile ? 'bottom' : 'right',
            },
            scrollTo: false,
            buttons: buildButtons(tour, false, false),
          },
        ];
      case 'NEW_CHAT':
        return [
          {
            title: i18n.t.productTour.newChat.step1.title,
            text: i18n.t.productTour.newChat.step1.text,
            modalOverlayOpeningPadding: 8,
            attachTo: {
              element: '.sidebar-button',
              on: 'top',
            },
            buttons: buildButtons(tour, false, false),
          },
        ];
    }
  };

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    exitOnEsc: false,
    keyboardNavigation: true,
    defaultStepOptions: {
      classes: 'fe-tourpilot-styles',
      modalOverlayOpeningRadius: 8,
      when: {
        show() {
          const currentStep = Shepherd.activeTour?.getCurrentStep();
          const currentStepElement = currentStep?.getElement();
          const footer = currentStepElement?.querySelector('.shepherd-footer');
          const progress = document.createElement('div');
          const liElements =
            Shepherd.activeTour?.steps.length === 1
              ? ''
              : Shepherd.activeTour?.steps
                  .map((step, index) => {
                    return `<li role='presentation' class='nav-dot ${index === Shepherd.activeTour?.steps.indexOf(currentStep!) ? '-active' : ''}'></li>`;
                  })
                  .join('');
          progress.className = 'shepherd-progress';
          progress.innerHTML = `<ul class='eva-3-nav-dots -md'>${liElements}</ul>`;
          footer?.prepend(progress);

          // Group buttons in footer
          const buttons = document.createElement('div');
          buttons.className = 'shepherd-buttons';
          footer?.querySelectorAll('.shepherd-button').forEach(button => {
            buttons.appendChild(button);
          });
          footer?.append(buttons);

          // Replace cross icon
          const crossButton = currentStepElement?.querySelector('.shepherd-cancel-icon');
          if (!!crossButton) {
            crossButton.innerHTML = '<i class="eva-3-icon-cross -eva-3-icon-xsm"></i>';
          }
        },
      },
      floatingUIOptions: {
        middleware: [offset(28)],
      },
      cancelIcon: {
        enabled: true,
        label: i18n.t.productTour.close,
      },
      scrollTo: true,
    },
  });

  setTimeout(() => {
    cbBeforeOpen();

    tour.on('complete', () => {
      saveFinishTour(true);
      cbAfterClose();
    });
    tour.on('cancel', () => {
      saveFinishTour(false);
      cbAfterClose();
    });

    triggerTrackEvent({
      event_type: 'system',
      event_name: params.startTour,
    });

    const steps = getStepsByType(type, tour);
    tour.addSteps(steps).start();
  }, 1500);
};

export const isFinishedTour = (type: ProductTourType) => {
  const params = paramsByType[type];
  const hasPreloadChat = typeof initParams.configString !== 'undefined' || getPreviousChat() !== null;
  return typeof localStorage === 'undefined' || localStorage.getItem(params.storageItem) === 'true' || hasPreloadChat;
};

export const initInitialTour = () => {
  if (!state.finishedInitialTour) {
    initTour('INITIAL', undefined, () => {
      state.finishedInitialTour = true;
    });
  }
};

export const initFlightCardsTour = (withFav: boolean) => {
  initTour('FLIGHT_CARDS', undefined, () => {
    initFavProductTour(withFav);
  });
};

export const initCopyMessageTour = () => {
  initTour('COPY_MESSAGE');
};

export const initFavProductTour = (enabled: boolean) => {
  if (enabled) {
    initTour('FAV_PRODUCT');
  }
};

export const initNewChatTour = (enabled: boolean) => {
  if (enabled) {
    initTour('NEW_CHAT');
  }
};
